'use client'

import { Button, Image, Spacer, Text } from '@vinted/web-ui'

import {
  businessAccountFromUserDto,
  BusinessAccountStatus,
} from '@marketplace-web/domain/business-accounts-data'
import { AuthModal } from '@marketplace-web/domain/identity'
import { navigateToPage } from '@marketplace-web/shared/browser'
import { useCookie } from '@marketplace-web/shared/cookies'
import { useTracking } from '@marketplace-web/shared/event-tracker'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { useSession } from '@marketplace-web/shared/session'
import { EMPTY_USER_IMAGE_NAME, useAsset } from '@marketplace-web/domain/assets'
import { UserImage } from '@marketplace-web/shared/ui-helpers'
import { findThumbnail } from '@marketplace-web/shared/utils'

import { clickEvent } from '_libs/common/event-tracker/events'
import LanguageSelector from 'components/LanguageSelector'

import { ClickableElement } from 'constants/tracking/clickable-elements'
import { Screen } from 'constants/tracking/screens'
import { logoutUser } from 'data/api/authentication/requests'

import { BUSINESS_REGISTER_PATH, ROUTES } from '../../constants/routes'
import SignUpButton from '../SignUpButton/SignUpButton'

type Props = {
  showRightButtons?: boolean
}

const ProNavBar = ({ showRightButtons }: Props) => {
  const { track } = useTracking()
  const translate = useTranslate()
  const asset = useAsset('assets/web-logo/default')
  const noPhotoAsset = useAsset('assets/no-photo')
  const cookies = useCookie()
  const { user } = useSession()

  const isLoggedIn = !!user
  const userPhoto = user?.photo
  const businessAccount = businessAccountFromUserDto(user)

  const handleLogout = async () => {
    const response = await logoutUser({ cookies })

    if (response) navigateToPage(ROUTES.BASE)
  }

  const handleConvertClick = () => {
    track(
      clickEvent({
        target: ClickableElement.ConvertToBusiness,
        screen: Screen.BusinessHomepage,
      }),
    )
  }

  const renderLogo = () => (
    <a
      className="u-flexbox u-align-items-center u-ui-padding-left-small business-accounts-navbar__logo"
      href={ROUTES.BASE}
    >
      <Image src={asset('logo.svg')} />
      <span className="business-accounts-navbar__logo-suffix">
        <Text as="span">{translate('business.title')}</Text>
      </span>
    </a>
  )

  const renderSignupButton = () => {
    if (!showRightButtons) return null

    return (
      <SignUpButton size={Button.Size.Small} inline text={translate('business.navbar.signup')} />
    )
  }

  const renderConvertUser = () => {
    if (!showRightButtons) return null

    return (
      <Button
        size={Button.Size.Small}
        styling={Button.Styling.Filled}
        inline
        text={translate('business.navbar.convert')}
        url={BUSINESS_REGISTER_PATH}
        onClick={handleConvertClick}
      />
    )
  }

  const renderLogoutButton = () => {
    if (!isLoggedIn) return null
    if (businessAccount?.status !== BusinessAccountStatus.WalletConversionInProgress) return null

    return (
      <Button
        size={Button.Size.Small}
        styling={Button.Styling.Flat}
        theme="amplified"
        inline
        text={translate('business.navbar.logout')}
        onClick={handleLogout}
      />
    )
  }

  const renderUserImage = () => {
    if (!isLoggedIn) return null

    return (
      <>
        <Spacer orientation={Spacer.Orientation.Vertical} size={Spacer.Size.Large} />
        <UserImage
          src={findThumbnail(userPhoto) || noPhotoAsset(EMPTY_USER_IMAGE_NAME)}
          size={Image.Size.Regular}
          styling={Image.Styling.Circle}
        />
      </>
    )
  }

  const renderLanguageSelector = () => (
    <div className="u-position-relative">
      <LanguageSelector isStandalone />
    </div>
  )

  const renderRightButtons = () => (
    <>
      {renderLogoutButton()}
      <div className="u-desktops-only">
        {!isLoggedIn && renderSignupButton()}
        {isLoggedIn && !businessAccount && renderConvertUser()}
        {renderUserImage()}
      </div>
      {renderLanguageSelector()}
    </>
  )

  return (
    <>
      <div className="business-accounts-navbar action-header">
        <div className="business-accounts-navbar__container">
          <div>{renderLogo()}</div>
          <div className="business-accounts-navbar__right-buttons">{renderRightButtons()}</div>
        </div>
      </div>
      <AuthModal />
    </>
  )
}

export default ProNavBar
